import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check, Minus, Plus } from "lucide-react";
import SizeSelector from "@/components/product/SizeSelector";
import ColourSelector from "@/components/product/ColourSelector";
import { PRODUCT } from "@/config/product";
import { SIZE_GROUPS, POGO_COLOURS, SIZE_NOT_SURE, PREORDER_COLOUR_IDS } from "@/config/pogoConfig";
import { STORE } from "@/config/store";
import { getVariantMap, createCheckoutUrl, getVariantContentId } from "@/lib/shopify";
import { trackBoth } from "@/lib/meta";

// Front-end cart only: the CHECKOUT button is a non-production placeholder.
// When Shopify connects, the selected variants (colour.id + size + qty per
// line item) map to Shopify cart line items.
// sizePrompt mode: opened with a colour but no size (sticky-bar preorder) —
// shows a compact pick-your-size panel that adds the pair on selection.
export default function CartDrawer({ open, items = [], onClose, sizePrompt = false, promptColour = null, onSizePick, onUpdateQty, onAddItem, onOpenSizeGuide }) {
  const [pickedSize, setPickedSize] = useState(null);
  const [bundleOpen, setBundleOpen] = useState(false);
  const [bundleColour, setBundleColour] = useState(null);
  const [bundleSize, setBundleSize] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [linePrices, setLinePrices] = useState(null);

  // Cart pricing source of truth = live Shopify variant prices (falls back
  // to the config price until the variant map loads).
  useEffect(() => {
    if (!items.length) return;
    let live = true;
    getVariantMap()
      .then((map) => {
        if (!live) return;
        const p = {};
        for (const i of items) {
          const eu = i.size.replace(/^EU\s+/i, "");
          const us = PRODUCT.shopify.euToUs[eu];
          const v = us && map[`${i.colour.name}|${us}`.toLowerCase()];
          if (v) p[`${i.colour.id}|${i.size}`] = v.price;
        }
        setLinePrices(p);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [items]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setPickedSize(null);
      setBundleOpen(false);
      setBundleColour(null);
      setBundleSize(null);
    }
  }, [open]);

  const promptActive = sizePrompt && items.length === 0 && promptColour;

  // First pair full price; from 2 pairs the bundle discount comes off once
  // (2 pairs = $199). The matching discount must exist in Shopify admin.
  const totalQty = items.reduce((s, i) => s + i.qty, 0);
  const bundleAmt = PRODUCT.shopify.bundleDiscountAmount ?? 0;
  const unitOf = (i) =>
    linePrices?.[`${i.colour.id}|${i.size}`] ?? PRODUCT.price;
  const extra = PRODUCT.price - bundleAmt;
  const subtotal =
    totalQty
      ? items.reduce((s, i) => s + unitOf(i) * i.qty, 0) -
        (totalQty >= 2 ? bundleAmt : 0)
      : null;
  const bundleSaving = totalQty >= 2 ? bundleAmt : 0;

  const checkout = async () => {
    // InitiateCheckout (standard): one per genuine checkout attempt, fired
    // on click — this is the only source for Shop Pay/shop.app checkouts,
    // where Shopify's own web pixel cannot run. Shopify may ALSO fire its
    // own InitiateCheckout when a merchant-domain checkout loads; those
    // carry Shopify's event_ids, so the two systems do NOT dedupe (known,
    // accepted). CheckoutClick stays as the internal click denominator.
    Promise.all(
      items.map((i) =>
        getVariantContentId(i.colour.name, i.size).catch(() => null)
      )
    ).then((ids) => {
      const resolved = ids.filter(Boolean);
      if (resolved.length !== items.length) {
        console.error(
          "[meta] InitiateCheckout: variant ID unresolved for",
          items.filter((_, x) => !ids[x]).map((i) => `${i.colour.name} ${i.size}`)
        );
      }
      const payload = {
        content_name: PRODUCT.title,
        content_ids: resolved.length ? resolved : undefined,
        content_type: "product",
        value: subtotal ?? undefined,
        currency: PRODUCT.currency,
        num_items: items.reduce((s, i) => s + i.qty, 0),
        items: items.map((i) => `${i.colour.name} ${i.size} x${i.qty}`).join(", "),
      };
      trackBoth("InitiateCheckout", payload);
      trackBoth("CheckoutClick", payload);
    });
    setCheckoutError(null);
    if (items.some((i) => i.size === SIZE_NOT_SURE)) {
      setCheckoutError("Pick a size for every pair before checkout.");
      return;
    }
    setCheckingOut(true);
    try {
      const variantMap = await getVariantMap();
      const lines = items.map((i) => {
        const eu = i.size.replace(/^EU\s+/i, "");
        const us = PRODUCT.shopify.euToUs[eu];
        const v = us && variantMap[`${i.colour.name}|${us}`.toLowerCase()];
        if (!v) throw new Error(`That ${i.colour.name} / ${i.size} pair isn't available right now.`);
        return { merchandiseId: v.id, quantity: i.qty };
      });
      const discount =
        totalQty >= 2 ? PRODUCT.shopify.bundleDiscountCode : null;
      const url = await createCheckoutUrl(lines, discount);
      window.location.assign(url);
    } catch (e) {
      setCheckingOut(false);
      setCheckoutError(
        e.message?.includes("pair")
          ? e.message
          : "Checkout couldn't start — please try again."
      );
    }
  };

  return (
    <AnimatePresence>
      {open && (items.length > 0 || promptActive) && (
        <div data-testid="cart-drawer" className="fixed inset-0 z-50">
          <motion.button
            data-testid="cart-backdrop"
            aria-label="Close bag"
            onClick={onClose}
            className="absolute inset-0 bg-ink/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label="Your bag"
            className="absolute right-0 top-0 h-full w-[92%] sm:w-full sm:max-w-md bg-ivory flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-ink/10">
              <h2 className="font-display uppercase text-2xl tracking-wide">
                Your cart
              </h2>
              <button
                data-testid="cart-close"
                onClick={onClose}
                aria-label="Close"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-ink text-ivory transition-transform duration-150 active:scale-90"
              >
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>

            {/* Order progress — cart half done, "Ordered" still to go */}
            <div data-testid="cart-progress" className="px-6 pt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-body font-bold text-[10px] tracking-[0.18em] uppercase text-ink/60">
                  Cart — half complete
                </span>
                <span className="font-body font-bold text-[10px] tracking-[0.18em] uppercase text-ink/35">
                  Ordered
                </span>
              </div>
              <div className="relative mx-2.5">
                <div className="h-1.5 rounded-full bg-ink/10 overflow-hidden">
                  <div className="h-full w-1/2 rounded-full bg-[#4ADE80]" />
                </div>
                <span
                  aria-hidden="true"
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-ivory border-2 border-[#4ADE80] inline-flex items-center justify-center"
                >
                  <Check className="w-3 h-3 text-[#22C55E]" strokeWidth={3.5} />
                </span>
                <span
                  aria-hidden="true"
                  data-testid="cart-progress-next"
                  className="absolute right-[-10px] top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-ink/15 border-2 border-ivory inline-flex items-center justify-center"
                >
                  <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4 space-y-4">
              {promptActive && (
                <div data-testid="cart-size-prompt">
                  <div className="flex gap-4 items-center rounded-2xl border-[1.5px] border-ink p-3">
                    <img
                      data-testid="prompt-item-image"
                      src={PRODUCT.colourImages[promptColour.id] || PRODUCT.images[0].src}
                      alt={`POSSO Work Clog in ${promptColour.name}`}
                      className="w-16 aspect-square object-cover rounded-xl border-2 border-ink shrink-0"
                      draggable="false"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-display uppercase text-xl leading-none">
                        {PRODUCT.title}
                      </p>
                      <p className="mt-1.5 text-sm text-ink/70">
                        {promptColour.name}
                      </p>
                      <p className="mt-1.5 font-display text-lg">
                        <span className="text-sm text-ink/40 line-through mr-1.5">
                          ${PRODUCT.launchPrice}
                        </span>
                        {PRODUCT.price ? `$${PRODUCT.price}` : "$XX"}
                      </p>
                    </div>
                  </div>
                  <p className="mt-4 font-body font-bold text-[11px] tracking-[0.22em] uppercase text-ink/50">
                    Almost there — pick your size
                  </p>
                  <div className="mt-2.5">
                    <SizeSelector
                      groups={SIZE_GROUPS}
                      value={pickedSize}
                      onChange={setPickedSize}
                      testIdPrefix="prompt-size"
                      onSizeGuide={onOpenSizeGuide}
                    />
                  </div>
                </div>
              )}
              {!promptActive && items.map((item, idx) => (
                <div
                  key={`${item.colour.id}-${idx}`}
                  data-testid={`cart-item-${idx + 1}`}
                  className="flex gap-4 items-center rounded-2xl border-[1.5px] border-ink p-3"
                >
                  <img
                    data-testid={`cart-item-image-${idx + 1}`}
                    src={PRODUCT.colourImages[item.colour.id] || PRODUCT.images[0].src}
                    alt={`POSSO Work Clog in ${item.colour.name}`}
                    className="w-16 aspect-square object-cover rounded-xl border-2 border-ink shrink-0"
                    draggable="false"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-display uppercase text-xl leading-none">
                      {PRODUCT.title}
                    </p>
                    <p
                      data-testid={`cart-item-variant-${idx + 1}`}
                      className="mt-1 text-sm text-ink/70"
                    >
                      {item.colour.name} · {item.size}
                    </p>
                    <div className="mt-1.5 inline-flex items-center gap-1 rounded-full border-2 border-ink/15 px-1 py-0.5">
                      <button
                        data-testid={`cart-item-minus-${idx + 1}`}
                        onClick={() => onUpdateQty?.(idx, -1)}
                        aria-label={item.qty <= 1 ? "Remove item" : "Decrease quantity"}
                        className="w-6 h-6 inline-flex items-center justify-center rounded-full transition-transform duration-150 active:scale-90"
                      >
                        <Minus className="w-3.5 h-3.5" strokeWidth={3} />
                      </button>
                      <span
                        data-testid={`cart-item-qty-${idx + 1}`}
                        className="w-6 text-center font-body font-bold text-sm"
                      >
                        {item.qty}
                      </span>
                      <button
                        data-testid={`cart-item-plus-${idx + 1}`}
                        onClick={() => onUpdateQty?.(idx, 1)}
                        aria-label="Increase quantity"
                        className="w-6 h-6 inline-flex items-center justify-center rounded-full transition-transform duration-150 active:scale-90"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                      </button>
                    </div>
                  </div>
                  <p data-testid={`cart-item-price-${idx + 1}`} className="font-display text-lg shrink-0">
                    <span className="text-sm text-ink/40 line-through mr-1.5">
                      ${PRODUCT.launchPrice * item.qty}
                    </span>
                    ${unitOf(item) * item.qty}
                  </p>
                </div>
              ))}

              {!promptActive && items.length > 0 && (
                <div
                  data-testid="bundle-offer"
                  className="rounded-2xl border-2 border-ink bg-[#FF6BAA] p-4 shadow-[3px_3px_0px_#111111]"
                >
                  <p className="font-display uppercase text-lg tracking-wide leading-tight">
                    Work Bestie Bundle
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="line-through text-ink/45 text-sm font-display">
                      ${(2 * PRODUCT.price).toFixed(2)}
                    </span>
                    <span className="font-display uppercase text-base tracking-wide">
                      2 pairs ${(2 * PRODUCT.price - bundleAmt).toFixed(0)}
                    </span>
                    <span className="ml-auto shrink-0 rounded-full bg-ivory border-2 border-ink px-3 py-1 font-display uppercase text-xs tracking-wide">
                      Save {Math.round((bundleAmt / (2 * PRODUCT.price)) * 100)}%
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-semibold leading-snug">
                    One for you, one for your work bestie — or both for you.
                    Second pair just ${extra.toFixed(2)}.
                  </p>
                  {!bundleOpen ? (
                    <button
                      data-testid="bundle-open"
                      onClick={() => {
                        setBundleColour(POGO_COLOURS.find((c) => c.id === "pink"));
                        setBundleOpen(true);
                      }}
                      className="mt-3 w-full min-h-[44px] rounded-full border-2 border-ink bg-ink text-ivory font-display uppercase text-sm tracking-wide transition-transform duration-150 active:scale-[0.97]"
                    >
                      Add a 2nd pair — ${extra.toFixed(2)}
                    </button>
                  ) : (
                    <div className="mt-3 space-y-3">
                      <ColourSelector
                        values={POGO_COLOURS.filter((c) =>
                          PREORDER_COLOUR_IDS.includes(c.id)
                        )}
                        value={bundleColour}
                        onChange={setBundleColour}
                        testIdPrefix="bundle-colour"
                        hideLabel
                      />
                      <SizeSelector
                        groups={SIZE_GROUPS}
                        value={bundleSize}
                        onChange={setBundleSize}
                        testIdPrefix="bundle-size"
                      />
                      <button
                        data-testid="bundle-add"
                        disabled={!bundleColour || !bundleSize}
                        onClick={() => {
                          if (!bundleColour || !bundleSize) return;
                          onAddItem?.({ colour: bundleColour, size: bundleSize, qty: 1 });
                          setBundleOpen(false);
                          setBundleColour(null);
                          setBundleSize(null);
                        }}
                        className="w-full min-h-[44px] rounded-full bg-ivory border-2 border-ink font-display uppercase text-sm tracking-wide transition-transform duration-150 active:scale-[0.97] disabled:opacity-40"
                      >
                        {bundleColour && bundleSize
                          ? `Add pair — $${extra.toFixed(2)}`
                          : "Pick a size"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="rounded-2xl border-2 border-ink bg-[#FFD84D] p-3">
                <p className="font-display uppercase text-xs tracking-wide">
                  This is a pre-order
                </p>
                <p data-testid="cart-dispatch" className="mt-1 text-sm font-medium">
                  Dispatches {STORE.dispatchWindow} 🎄
                </p>
                <p className="mt-1 text-xs text-ink/70 leading-snug">
                  <span className="underline decoration-[#FF6BAA] decoration-2 underline-offset-2 font-semibold text-ink">
                    Limited quantity available
                  </span>{" "}
                  — continue to checkout to secure your pair!
                </p>
              </div>
            </div>

            {promptActive ? (
              <div className="border-t border-ink/10 px-6 py-5">
                <p
                  data-testid="prompt-free-shipping"
                  className="mb-2.5 text-center text-xs font-semibold text-ink/60"
                >
                  Free standard shipping AU-wide — dispatches {STORE.dispatchWindow} 🎄
                </p>
                <button
                  data-testid="prompt-add-button"
                  onClick={() => {
                    if (!pickedSize) return;
                    onSizePick?.(pickedSize);
                  }}
                  disabled={!pickedSize}
                  className="w-full min-h-[60px] rounded-full bg-[#FF6BAA] text-ink font-display text-xl uppercase tracking-wide inline-flex items-center justify-center gap-2 border-2 border-ink shadow-[3px_3px_0px_#111111] transition-transform duration-150 active:scale-[0.97] disabled:opacity-40"
                >
                  {pickedSize
                    ? `Add to cart — $${PRODUCT.price}`
                    : "Pick a size above"}
                </button>
              </div>
            ) : (
            <div className="border-t border-ink/10 px-6 py-4">
              {bundleSaving > 0 && (
                <div
                  data-testid="cart-bundle-saving"
                  className="flex items-center justify-between mb-1"
                >
                  <span className="font-body font-bold text-[11px] tracking-[0.18em] uppercase text-[#16A34A]">
                    Bundle saving
                  </span>
                  <span className="font-display text-sm text-[#16A34A]">
                    −${bundleSaving.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="font-display uppercase tracking-wide">
                  Subtotal
                </span>
                <span data-testid="cart-subtotal" className="font-display text-xl">
                  {subtotal ? (
                    <>
                      <span className="text-sm text-ink/40 line-through mr-1.5">
                        ${PRODUCT.launchPrice * totalQty}
                      </span>
                      ${Number.isInteger(subtotal) ? subtotal : subtotal.toFixed(2)}{" "}
                      {PRODUCT.currency}
                    </>
                  ) : (
                    "$XX AUD"
                  )}
                </span>
              </div>
              <p data-testid="cart-founding-note" className="mt-1 text-xs text-ink/60 font-medium">
                Founding preorder perks — save ${PRODUCT.launchPrice - PRODUCT.price} before launch + free shipping AU-wide.
              </p>
              {checkoutError && (
                <p
                  data-testid="cart-checkout-error"
                  className="mt-2 text-xs font-semibold text-[#DC2626]"
                >
                  {checkoutError}
                </p>
              )}
              <button
                data-testid="cart-checkout-button"
                onClick={checkout}
                disabled={checkingOut}
                className="mt-3 w-full min-h-[56px] rounded-full bg-ink text-ivory font-display text-xl uppercase tracking-wide inline-flex items-center justify-center gap-2 transition-transform duration-150 active:scale-[0.97] disabled:opacity-60"
              >
                {checkingOut ? (
                  "Opening secure checkout…"
                ) : (
                  <>
                    Continue to checkout <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                  </>
                )}
              </button>
            </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
